import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Scale, Loader2, RefreshCw } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const API = process.env.REACT_APP_BACKEND_URL;
const PAGE = 5;

/** Admin → Εργαλεία: Εναρμόνιση τύπου δικαστηρίου με το πινάκιο — (ΠΟΛ)/(ΜΟΝ) στο πινάκιο ≠ court_type → επιβεβαίωση ανά 5. */
const CourtTypeFixCard = () => {
  const [data, setData] = useState(null);
  const [choice, setChoice] = useState({});
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const r = await axios.get(`${API}/api/admin/court-type-mismatch/preview`, { params: { skip: 0, limit: PAGE } });
      setData(r.data);
      const c = {}; (r.data.items || []).forEach((it) => { c[it.id] = it.suggested_court_type; }); setChoice(c);
    } catch (e) { toast.error(e.response?.data?.detail || "Σφάλμα φόρτωσης"); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const apply = async () => {
    const items = (data?.items || []).map((it) => ({ case_id: it.id, court_type: choice[it.id] || it.suggested_court_type }));
    if (!items.length) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/admin/court-type-mismatch/apply`, { items });
      toast.success(`Διορθώθηκαν ${r.data.updated} υποθέσεις (με επανυπολογισμό προθεσμιών)`); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Σφάλμα"); }
    finally { setBusy(false); }
  };

  const items = data?.items || [];
  return (
    <Card data-testid="court-type-fix-card">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2"><Scale className="w-4 h-4 text-purple-700" />Εναρμόνιση Τύπου Δικαστηρίου με το Πινάκιο</CardTitle>
        <CardDescription>Ο τύπος δικαστηρίου εξαρτάται από το πινάκιο: όταν το πινάκιο περιέχει (ΠΟΛ) και η υπόθεση είναι Μονομελές (ή αντίστροφα), εμφανίζεται εδώ ανά 5 υποθέσεις για επιβεβαίωση του ορθού τύπου. Με την επιβεβαίωση επανυπολογίζονται οι προθεσμίες.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-2 text-xs text-slate-600">
          <span data-testid="court-type-fix-total">Ασυμφωνίες: <b>{data?.total ?? "…"}</b></span>
          <Button size="sm" variant="ghost" onClick={load} disabled={busy} data-testid="court-type-fix-refresh"><RefreshCw className="w-4 h-4" /></Button>
        </div>
        {items.length > 0 ? (
          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full text-xs" data-testid="court-type-fix-table">
              <thead className="bg-slate-50 text-slate-600"><tr><th className="p-2 text-left">ΓΑΚ</th><th className="p-2 text-left">Πινάκιο</th><th className="p-2 text-left">Τρέχων τύπος</th><th className="p-2 text-left">Ορθός τύπος (από πινάκιο)</th></tr></thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.id} className="border-t" data-testid={`court-type-fix-row-${it.id}`}>
                    <td className="p-2 font-mono">{it.gak_number}/{it.gak_year}</td>
                    <td className="p-2">{it.last_hearing_date}</td>
                    <td className="p-2 text-red-700">{it.current_court_type || "—"}</td>
                    <td className="p-2">
                      <Select value={choice[it.id] || it.suggested_court_type} onValueChange={(v) => setChoice({ ...choice, [it.id]: v })}>
                        <SelectTrigger className="h-8 w-40" data-testid={`court-type-fix-select-${it.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="Μονομελές">Μονομελές</SelectItem><SelectItem value="Πολυμελές">Πολυμελές</SelectItem></SelectContent>
                      </Select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : data && <p className="text-xs text-emerald-700" data-testid="court-type-fix-empty">Δεν υπάρχουν ασυμφωνίες τύπου δικαστηρίου με πινάκιο.</p>}
        {items.length > 0 && (
          <Button size="sm" onClick={apply} disabled={busy} data-testid="court-type-fix-apply">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Scale className="w-4 h-4 mr-1" />}Επιβεβαίωση & διόρθωση ({items.length})
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default CourtTypeFixCard;
